/**
 * WP-UPDATE — deciding whether the desktop app needs reinstalling.
 *
 * The desktop app is a thin Electron shell around the live site. Everything
 * else updates the moment the NAS is deployed; the shell itself only changes
 * when somebody installs a new one. These pure functions compare the version
 * the shell is running against the version sitting in the installers folder,
 * and say whether to ask — and whether the answer may be "later".
 *
 * Kept out of the component on purpose: this repo has no React component test
 * harness, so the decision has to live somewhere a unit test can reach it.
 */

/**
 * Split a version string into numbers, forgivingly.
 *
 * "1.2.3" -> [1, 2, 3]; "v1.2" -> [1, 2]; "1.1.0-universal" -> [1, 1, 0];
 * anything with no digits at all -> [] (which compares as 0.0.0, i.e. older
 * than every real release). Nothing here throws: the inputs are a filename, an
 * Electron call and a text file, and any of them can be missing or nonsense.
 */
export const parseVersion = (value) => {
  if (value === null || value === undefined) return [];
  return String(value)
    .split(/[^0-9]+/)
    .filter((part) => part !== '')
    .map((part) => {
      const n = parseInt(part, 10);
      return Number.isFinite(n) ? n : 0;
    });
};

/**
 * Compare two version strings: -1 if a is older, 1 if a is newer, 0 if equal.
 *
 * Missing parts count as zero, so "1.1" and "1.1.0" are the same version, and
 * garbage ("", null, "banana") is treated as 0.0.0 — older than any release,
 * which is exactly what we want for a shell too old to report its version.
 */
export const compareVersions = (a, b) => {
  const left = parseVersion(a);
  const right = parseVersion(b);
  const len = Math.max(left.length, right.length);
  for (let i = 0; i < len; i += 1) {
    const l = left[i] === undefined ? 0 : left[i];
    const r = right[i] === undefined ? 0 : right[i];
    if (l < r) return -1;
    if (l > r) return 1;
  }
  return 0;
};

/**
 * Electron's platform name ('darwin', 'win32') as the desktop-client endpoints
 * spell it ('mac', 'win'). Already-correct values pass straight through, so a
 * caller can hand this either form.
 */
export const normalisePlatform = (value) => {
  const v = String(value || '').toLowerCase();
  // 'darwin' contains "win" — check the Mac names FIRST or every Mac is
  // offered the Windows installer.
  if (v.includes('darwin') || v.includes('mac')) return 'mac';
  if (v.includes('win')) return 'win';
  return 'mac';
};

/**
 * Should the update banner appear, and can it be dismissed?
 *
 * @param {object} args
 * @param {string|null} args.appVersion     what this shell reports (null = an old
 *                                          shell whose bridge has no getAppVersion)
 * @param {string|null} args.serverVersion  the version of the installer on the NAS
 * @param {string|null} args.minimumVersion the oldest build still allowed to work
 * @param {string|null} args.platform       'mac' / 'win' / Electron's raw name
 * @returns {{show: boolean, required: boolean, reason: string, platform: string}}
 *
 * Rules, in order:
 *   - this shell cannot say which version it is -> it predates the version
 *     bridge (1.0.0 or older), so treat it as older than anything: require it
 *     if there is a minimum at all, otherwise show it whenever the NAS has an
 *     installer to point at.
 *   - below the minimum -> required, no Later button. This is checked even when
 *     there is no installer for this platform yet (only the Mac .dmg is in the
 *     folder today): a shell the floor has locked out must still say so, or a
 *     Windows machine would sail past a minimum nobody can see.
 *   - no server version (endpoint down, no installer uploaded yet) -> say
 *     nothing further. Nobody is nagged about an optional update they cannot be
 *     told how to install.
 *   - the NAS has something newer -> show, Later allowed.
 *   - otherwise -> up to date, say nothing.
 */
export const decideUpdatePrompt = ({
  appVersion = null,
  serverVersion = null,
  minimumVersion = null,
  platform = null,
} = {}) => {
  const result = (show, required, reason) => ({
    show,
    required,
    reason,
    platform: normalisePlatform(platform),
  });

  const haveServer = parseVersion(serverVersion).length > 0;
  const haveApp = parseVersion(appVersion).length > 0;
  const haveMinimum = parseVersion(minimumVersion).length > 0;

  // The floor is judged FIRST, on appVersion alone. It must not depend on there
  // being an installer for this platform: the installers folder holds only the
  // Mac .dmg today, so a Windows shell gets serverVersion = null, and checking
  // the server first would let a locked-out machine carry on in silence.
  if (!haveApp) {
    // An old shell. It is definitely not the build on the NAS, and if a floor
    // has been set it is definitely below it.
    if (haveMinimum) return result(true, true, 'unknown-app-version-below-minimum');
    if (!haveServer) return result(false, false, 'no-server-version');
    return result(true, false, 'unknown-app-version');
  }

  if (haveMinimum && compareVersions(appVersion, minimumVersion) < 0) {
    return result(true, true, 'below-minimum');
  }

  // Past the floor, an update is only ever optional — and an optional update
  // needs a version to offer.
  if (!haveServer) return result(false, false, 'no-server-version');

  if (compareVersions(serverVersion, appVersion) > 0) {
    return result(true, false, 'newer-available');
  }

  return result(false, false, 'up-to-date');
};

/**
 * "120 of 280 MB" for the progress line. Falls back to just the received size
 * while the total is unknown (some servers send no content-length).
 */
export const formatDownloadProgress = (receivedBytes, totalBytes) => {
  const mb = (n) => Math.round((Number(n) || 0) / (1024 * 1024));
  if (!totalBytes || Number(totalBytes) <= 0) return `${mb(receivedBytes)} MB`;
  return `${mb(receivedBytes)} of ${mb(totalBytes)} MB`;
};

/**
 * "Later", remembered for this launch of the app.
 *
 * Every page renders its own <Layout>, so moving between pages unmounts the
 * banner and mounts a fresh one. A dismissal held in the component's own state
 * would therefore come back on the next click of the menu. This module is
 * loaded once per launch, so a flag living here lasts exactly as long as the
 * app window does, and nothing is written to disk or to browser storage — the
 * banner is back the next time the app is opened. That is the point: three
 * people share this app and an update that can be permanently dismissed never
 * happens.
 */
let dismissedThisLaunch = false;

export const wasUpdateDismissedThisLaunch = () => dismissedThisLaunch;

export const dismissUpdateForThisLaunch = () => { dismissedThisLaunch = true; };
